import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, FormFeedback, Form } from "reactstrap";
import { loadCodelists, COLLECTION_SKIP_REASON_OPTIONS } from "api/modules/common.option";

interface SkipReasonModalProps {
  isOpen: boolean;
  toggle: () => void;
  subjectBarcode: string;
  onSubmit: (reason: string) => void;
  isPending: boolean;
}

const MAX_REASON_LENGTH = 100;
// Pattern allows standard letters, numbers, spaces, and safe punctuation (. , - _ ( ) / : ' ")
const SAFE_TEXT_PATTERN = /^[a-zA-Z0-9\s.,\-_()/:'"]*$/;

const SkipReasonModal: React.FC<SkipReasonModalProps> = ({
  isOpen,
  toggle,
  subjectBarcode,
  onSubmit,
  isPending,
}) => {
  const [reason, setReason] = useState("");
  const [validationError, setValidationError] = useState("");
  const [presetReasons, setPresetReasons] = useState<string[]>([]);

  useEffect(() => {
    loadCodelists(["COLLECTION_SKIP_REASON_OPTIONS"]).then(() => {
      if (COLLECTION_SKIP_REASON_OPTIONS.length > 0) {
        setPresetReasons(COLLECTION_SKIP_REASON_OPTIONS.map((opt) => opt.label));
      }
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setValidationError("");
    }
  }, [isOpen]);

  const handleReasonChange = (val: string) => {
    if (val.length > MAX_REASON_LENGTH) {
      setValidationError(`Maximum length of ${MAX_REASON_LENGTH} characters exceeded.`);
      return;
    }

    if (val && !SAFE_TEXT_PATTERN.test(val)) {
      setValidationError("Contains invalid characters. Only letters, numbers, spaces and standard punctuation allowed.");
    } else {
      setValidationError("");
    }

    setReason(val);
  };

  const handlePresetSelect = (preset: string) => {
    setReason(preset);
    setValidationError("");
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidationError("Please select or type a reason for skipping.");
      return;
    }
    if (validationError) return;
    onSubmit(trimmed);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      className="common_modal"
      style={{ maxWidth: "520px", width: "95%" }}
    >
      <ModalHeader toggle={toggle} className="border-bottom py-3 px-3 bg-light">
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: "36px", height: "36px", backgroundColor: "#fef3c7", color: "#d97706" }}
          >
            <i className="ri-skip-forward-line fs-18"></i>
          </div>
          <div>
            <h6 className="fw-bold text-dark mb-0 fs-15" style={{ lineHeight: "1.2" }}>Skip Subject Collection</h6>
            <small className="text-muted" style={{ fontSize: "11.5px" }}>
              Provide a valid reason to skip this subject
            </small>
          </div>
        </div>
      </ModalHeader>

      <Form onSubmit={handleSubmit}>
        <ModalBody className="py-3 px-3">
          {/* Subject info badge */}
          <div
            className="d-flex align-items-center justify-content-between mb-3"
            style={{
              backgroundColor: "#fffbe6",
              border: "1px solid #ffe58f",
              color: "#d97706",
              borderRadius: "8px",
              padding: "10px 14px",
            }}
          >
            <div className="d-flex align-items-center gap-2" style={{ fontSize: "13px" }}>
              <i className="ri-error-warning-fill fs-16 me-1" style={{ color: "#d97706" }}></i>
              <span>
                Subject: <strong className="text-dark fs-14">{subjectBarcode || "-"}</strong>
              </span>
            </div>
            <span
              className="badge text-dark px-2.5 py-1 fw-bold"
              style={{ fontSize: "10px", letterSpacing: "0.5px", backgroundColor: "#fde68a" }}
            >
              SKIP ACTION
            </span>
          </div>

          {/* Preset Buttons */}
          <div className="mb-3">
            <label className="form-label fw-semibold text-dark mb-2" style={{ fontSize: "12.5px" }}>
              Select Reason Preset
            </label>
            <div className="d-flex flex-wrap gap-2">
              {presetReasons.map((option) => {
                const isSelected = reason === option;
                return (
                  <button
                    key={option}
                    type="button"
                    className={`btn btn-sm ${isSelected
                      ? "btn-warning text-dark fw-bold shadow-sm"
                      : "btn-outline-secondary bg-white text-secondary"
                      }`}
                    style={{
                      borderRadius: "20px",
                      fontSize: "12px",
                      padding: "5px 14px",
                      borderColor: isSelected ? "#f59e0b" : "#cbd5e1",
                      transition: "all 0.15s ease-in-out",
                    }}
                    onClick={() => handlePresetSelect(option)}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Textarea with Validation & Counter */}
          <div className="mb-1">
            <div className="d-flex justify-content-between align-items-center mb-1.5">
              <label className="form-label fw-semibold text-dark mb-0" style={{ fontSize: "12.5px" }}>
                Reason Details / Remarks <span className="text-danger">*</span>
              </label>
              <span
                className={`style_counter ${reason.length >= MAX_REASON_LENGTH ? "text-danger fw-bold" : "text-muted"
                  }`}
                style={{ fontSize: "11.5px" }}
              >
                {reason.length} / {MAX_REASON_LENGTH}
              </span>
            </div>
            <Input
              type="textarea"
              rows={3}
              placeholder="Type custom reason or select a preset above..."
              value={reason}
              maxLength={MAX_REASON_LENGTH}
              invalid={Boolean(validationError)}
              onChange={(e) => handleReasonChange(e.target.value)}
              style={{
                fontSize: "13px",
                borderRadius: "8px",
                borderColor: validationError ? "#ef4444" : "#cbd5e1",
                resize: "none",
              }}
            />
            {validationError && (
              <FormFeedback className="d-block mt-1" style={{ fontSize: "11.5px" }}>
                <i className="ri-error-warning-line me-1"></i>
                {validationError}
              </FormFeedback>
            )}
          </div>
        </ModalBody>

        <ModalFooter className="bg-light border-top py-2.5 px-3 d-flex justify-content-end gap-2">
          <Button
            size="sm"
            type="button"
            className="px-3 fw-bold border shadow-sm d-inline-flex align-items-center gap-1"
            style={{
              backgroundColor: "#fef2f2",
              color: "#dc2626",
              borderColor: "#fecaca",
              borderRadius: "6px",
            }}
            onClick={toggle}
            disabled={isPending}
          >
            <i className="ri-close-line fs-14 align-middle"></i>
            Cancel
          </Button>
          <Button
            size="sm"
            color="warning"
            type="submit"
            className="text-dark fw-bold px-3 shadow-sm border-0"
            style={{ backgroundColor: "#f59e0b" }}
            disabled={!reason.trim() || Boolean(validationError) || isPending}
          >
            {isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Submitting...
              </>
            ) : (
              <>
                <i className="ri-checkbox-circle-fill me-1 fs-14 align-middle"></i>
                Confirm Skip
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default SkipReasonModal;
